import type { ForecastItem } from '../api/types';
import { Badge, Button, Drawer } from './ui';
import { formatAmount, formatDate, formatMonthKey, formatRenewal } from '../lib/format';

/**
 * The drawer behind a forecast pill.
 *
 * A forecast is not a payment — there is no event, no bill, no rubles — so the
 * panel states what the *assumption* is and offers the three moves it implies:
 * book the renewal now (the common case, the month has actually been paid),
 * look at the subscription, or stop forecasting this one. The "next payment"
 * block comes from the same stepping the pills come from, so the two can never
 * disagree.
 */
export function ForecastDrawer({
  item,
  onClose,
  onRegisterPayment,
  onOpenSubscription,
  onHide,
  hidden,
}: {
  item: ForecastItem;
  onClose: () => void;
  /** Prefill the create-payment form with this forecast's amount and date. */
  onRegisterPayment: () => void;
  /** Jump to the subscription list, filtered to this subscription. */
  onOpenSubscription: () => void;
  /** Persist "no forecast for this subscription" and close. */
  onHide: () => void;
  /** True when this subscription's forecasts are already switched off. */
  hidden?: boolean;
}) {
  const months = item.months.length;
  const rowTotal = Object.entries(item.perCurrencyTotals)
    .map(([currency, amount]) => formatAmount(amount, currency))
    .join(' + ');

  return (
    <Drawer
      open
      onClose={onClose}
      title={item.name}
      description={item.groupName ? `${item.groupName} · forecast` : 'Forecast'}
    >
      <div className="forecast-next">
        <div className="forecast-next__label">Next payment</div>
        {item.next ? (
          <>
            <div className="forecast-next__amount">
              {formatAmount(item.next.amount, item.next.currency)}
            </div>
            <div className="forecast-next__date">
              {formatDate(item.next.date)} · {formatRenewal(item.next.amount, item.next.currency, item.renewalPeriod)}
            </div>
          </>
        ) : (
          <div className="forecast-next__date">
            Nothing ahead — the renewal chain stops at the Dec 2026 cap.
          </div>
        )}
      </div>

      <dl className="dl">
        <dt>Forecast</dt>
        <dd>
          {months} month{months === 1 ? '' : 's'} in this window · {rowTotal || '—'}
        </dd>
        {months > 0 ? (
          <>
            <dt>Months</dt>
            <dd>{item.months.map((key) => formatMonthKey(key)).join(', ')}</dd>
          </>
        ) : null}
        <dt>Status</dt>
        <dd>
          <Badge tone="brand">assumed</Badge>{' '}
          <span className="cell-muted">renewals assumed to continue; nothing is booked yet</span>
        </dd>
      </dl>

      <div className="forecast-menu">
        <Button variant="primary" onClick={onRegisterPayment} disabled={!item.next}>
          Register payment now
        </Button>
        <Button variant="secondary" onClick={onOpenSubscription}>
          Open subscription
        </Button>
        <Button variant="ghost" onClick={onHide}>
          {hidden ? 'Show forecasts for this subscription' : 'Hide forecasts for this subscription'}
        </Button>
      </div>
      <p className="state__hint">
        {hidden
          ? 'Hidden forecasts stay hidden on this browser until you switch them back on.'
          : 'Hiding keeps the row and its real payments — only the dotted assumptions go.'}
      </p>
    </Drawer>
  );
}
